import React, { Component } from 'react';
import type { IEventBus, TrackMethod } from '@shore/types';
import type {
  AppShellApiClient,
  ErrorLogger,
  Manifest,
} from '../../shared/types';

import Tracer from '../../utils/tracer';
import getGlobalValue from '../../utils/get-global-value';
import EVENTS from '../../constants/event-bus-events';
import FeatureRollout from '../../utils/feature-rollout';
import { NOOP } from '../../constants';
import { isAllowedToLoadAppName } from '../../utils';
import {
  GRAVITY_ACTIVATION_CLASS,
  GRAVITY_DESIGN_APPS,
} from '../../shared/constants/merchant';
import type { getAppSettings } from '../../selectors';
import type { AppAction } from '../../actions';

export type AppSettings = ReturnType<typeof getAppSettings>;

export interface MicroAppInstance {
  destroy: () => void;
  dispatchAction?: (action: { type: string; payload?: unknown }) => void;
}

interface LoadAppParams {
  name: string;
  apiClient: AppShellApiClient;
  trackMethod: TrackMethod;
  settings: AppSettings;
  pathAndQuery: string;
  useHashHistory: boolean;
  routingType: string;
  modal: Record<string, unknown>;
  eventBus: {
    on: IEventBus['on'];
    off: IEventBus['off'];
    emit: (eventName: string, payload?: Record<string, unknown>) => void;
  };
  onAction: (action: object | string) => void;
  container: HTMLElement | null;
  logError: (
    error: string,
    extraTagsFromMicroApp?: Record<string, unknown>
  ) => void;
}

// Deliberately NOT app-manifest/app-loader.ts's LoadAppResult: this is the
// consumer's view of the same object — legacy full apps resolve create() to an
// instance with destroy(), not MicroAppModule's { render } contract.
interface LoadedApp {
  // Real micro-app create() functions are async (confirmed against
  // customers-app's own create(), which returns Promise<{ destroy }>).
  create: () => Promise<MicroAppInstance | undefined>;
  manifest: Pick<Manifest, 'lastModified'>;
}

export type LoadApp = (params: LoadAppParams) => Promise<LoadedApp>;

export interface AppLoaderProps {
  name?: string | null;
  eventBus: IEventBus;
  disabled?: boolean;
  onInstance: (instance: MicroAppInstance | null | undefined) => void;
  onLoadingApp?: (isLoadingApp: boolean) => void;
  onAppAction?: (name: string, action: AppAction) => void;
  onError?: (error: unknown, extra?: Record<string, unknown>) => void;
  onDocumentOutdated?: () => void;
  settings: AppSettings;
  pathAndQuery?: string | null;
  useHashHistory?: boolean;
  routingType?: string;
  modal?: Record<string, unknown>;
  loadApp: LoadApp;
  documentDate?: string | null;
  trackMethod?: TrackMethod;
  apiClient?: AppShellApiClient;
  errorLogger: ErrorLogger;
  class?: string;
}

const moment = getGlobalValue('moment') as (
  date?: string | null
) => { format(fmt: string): string } & { valueOf(): number };

function isAppLoaderDeprecatedFor(appName: string): boolean {
  // This function basically means that the given micro app is now rendered by MicroApp component, in accordance to the new app-shell architecture design. For more info, check this ADR: https://www.notion.so/shorewiki/App-Shell-architecture-redesign-a842d1054de44867a9fa9a1228dab64d

  return !FeatureRollout.isActive('services-app-refactor')
    ? ['subscription'].includes(appName)
    : ['services', 'subscription'].includes(appName);
}

const eventuallyDestroyInstance = async (
  instancePromise?: Promise<MicroAppInstance | null | undefined> | null
): Promise<void> => {
  if (!instancePromise) return;
  const instance = await instancePromise;
  if (!instance) return;
  Tracer.clearAppConfig();
  instance.destroy();
};

const isAllowedToLoad = (
  isFreeProduct?: boolean,
  name?: string | null
): boolean => (isFreeProduct ? isAllowedToLoadAppName(name) : true);

// Still needed after the class below stopped reading `source`: bootstrap
// resolves the brand sheet from it, so holding here keeps a Gravity app from
// painting against the wrong brand.
const isAwaitingActivation = (
  settings: AppSettings,
  name?: string | null
): boolean =>
  GRAVITY_DESIGN_APPS.includes(name as string) &&
  settings?.source === undefined;

class AppLoader extends Component<AppLoaderProps> {
  private baseRef: React.RefObject<HTMLDivElement>;

  private appPromise?: Promise<MicroAppInstance | null | undefined> | null;

  constructor(props: AppLoaderProps) {
    super(props);

    this.baseRef = React.createRef();
  }

  componentDidMount() {
    this.startAppIfReady(this.props);
  }

  startAppIfReady(props: AppLoaderProps) {
    const { settings, name } = props;
    if (isAwaitingActivation(settings, name)) return;
    if (isAllowedToLoad(settings.isFreeProduct, name)) {
      this.setAppPromise(this.loadAndCreateApp(props), props.name);
    }
  }

  getComputedClassName(props: AppLoaderProps): string {
    let className = props.class || '';

    const appName = props.name;

    // Activation is per-app; the brand sheet is what varies per merchant.
    if (GRAVITY_DESIGN_APPS.includes(appName as string)) {
      className = className
        ? `${className} ${GRAVITY_ACTIVATION_CLASS}`
        : GRAVITY_ACTIVATION_CLASS;
    }

    return className;
  }

  UNSAFE_componentWillReceiveProps(nextProps: AppLoaderProps) {
    // Prop update logic:
    // 1. App switch (`name`/`disabled` changed) -> Destroy old, start new.
    // 2. Route change (only `pathAndQuery` changed) -> Forward to running app.
    // 3. Deferred start (no app running yet) -> Try loading now that props are ready.
    const { name, pathAndQuery, disabled } = this.props;
    const {
      name: nextName,
      disabled: nextDisabled,
      settings: nextSettings,
      pathAndQuery: nextPathAndQuery,
    } = nextProps;
    if (name !== nextName || disabled !== nextDisabled) {
      if (isAllowedToLoad(nextSettings.isFreeProduct, nextName)) {
        eventuallyDestroyInstance(this.appPromise);
        this.appPromise = null;
        this.startAppIfReady(nextProps);
      }
    } else if (name === nextName && pathAndQuery !== nextPathAndQuery) {
      // If `appPromise` is undefined (awaiting activation), `loadAndCreateApp` will pick up the latest `pathAndQuery` later.
      this.appPromise?.then((instance) => {
        if (instance && 'dispatchAction' in instance) {
          instance.dispatchAction?.({
            type: 'ROUTE_CHANGE',
            payload: nextPathAndQuery,
          });
        }
      });
    } else if (!this.appPromise) {
      this.startAppIfReady(nextProps);
    }

    // set class directly in DOM if changed
    const currentClassName = this.getComputedClassName(this.props);
    const nextClassName = this.getComputedClassName(nextProps);
    if (currentClassName !== nextClassName) {
      this.baseRef.current!.className = nextClassName;
    }
  }

  // we don't want rerender here and manually take care of updating the DOM,
  // see componentWillReceiveProps
  shouldComponentUpdate() {
    return false;
  }

  setAppPromise(
    promise: Promise<MicroAppInstance | null | undefined>,
    appName?: string | null
  ) {
    const loadingStart = Date.now();

    this.appPromise = promise;

    function trackLoadingTime() {
      Tracer.addAction(`${appName}.micro_app.loaded`, {
        microApp: { loadingTimeInMs: Date.now() - loadingStart },
      });
    }

    if (appName) {
      promise.then(this.props.onInstance).then(trackLoadingTime);
    } else {
      promise.then(this.props.onInstance);
    }
  }

  async loadAndCreateApp({
    name,
    eventBus,
    disabled = false,
    onLoadingApp = NOOP,
    onAppAction = NOOP,
    onError = NOOP,
    onDocumentOutdated = NOOP,
    settings,
    pathAndQuery,
    useHashHistory = false,
    routingType = 'path',
    modal = {},
    loadApp,
    documentDate = null,
    trackMethod = NOOP,
    apiClient = {} as AppShellApiClient,
    errorLogger,
  }: AppLoaderProps): Promise<MicroAppInstance | null | undefined> {
    if (!name || disabled || isAppLoaderDeprecatedFor(name)) return null;

    const logError = (
      error: unknown,
      extraTagsFromMicroApp: Record<string, unknown> = {}
    ) => {
      errorLogger.log(
        error instanceof Error ? error : new Error(String(error)),
        {
          tags: {
            app: name,
          },
        },
        extraTagsFromMicroApp
      );
    };

    onLoadingApp(true);
    try {
      this.props.eventBus.emit(EVENTS.CLOSE_MENU);

      const { create, manifest } = await loadApp({
        name,
        apiClient,
        trackMethod,
        settings,
        pathAndQuery: pathAndQuery ?? '',
        useHashHistory,
        routingType,
        modal,
        eventBus: {
          on: eventBus.on,
          off: eventBus.off,
          emit: (eventName: string, payload: Record<string, unknown> = {}) => {
            eventBus.emit(eventName, {
              payload,
              metadata: { appName: name, event: eventName },
            });
          },
        },
        onAction: (action) => {
          onAppAction(name, action as AppAction);
        },
        container: this.baseRef.current,
        logError,
      });

      Tracer.setAppName(name);

      const appYoungerThanDocument =
        !!documentDate && moment(manifest.lastModified) > moment(documentDate);

      // prevent app creation if app is younger than document
      if (appYoungerThanDocument) {
        onDocumentOutdated();
        return null;
      }

      try {
        return await create();
      } catch (createError) {
        onError(createError, { stage: 'create', app: name });
        return null;
      }
    } catch (error) {
      onError(error, { stage: 'load', app: name });
      return null;
    } finally {
      onLoadingApp(false);
    }
  }

  render() {
    return (
      <div
        ref={this.baseRef}
        className={this.getComputedClassName(this.props)}
      />
    );
  }
}

export default AppLoader;
